<template>
    <section class="user-card-section notification-device-settings">
        <div class="notification-device-settings__header">
            <div>
                <h6>Уведомления на этом устройстве</h6>
                <div class="small text-muted mt-1">
                    Разрешение и подписка сохраняются отдельно для каждого браузера и устройства.
                </div>
            </div>
            <span
                class="notification-device-settings__status"
                :class="`notification-device-settings__status--${statusVariant}`"
            >
                <i :class="statusIcon" aria-hidden="true"></i>
                {{ statusLabel }}
            </span>
        </div>

        <div v-if="isLoading" class="notification-device-settings__loading">
            <b-spinner small class="mr-2"></b-spinner>
            Проверяем настройки
        </div>

        <template v-else>
            <b-alert
                v-if="errorMessage"
                show
                variant="danger"
                class="py-2"
            >
                {{ errorMessage }}
            </b-alert>

            <p class="notification-device-settings__description">
                {{ statusDescription }}
            </p>

            <InstallAppPreview
                v-if="showInstallOffer && usesModernUi"
                size="mini"
                class="notification-device-settings__install-preview"
            />

            <div class="notification-device-settings__actions">
                <b-button
                    v-if="canEnable"
                    variant="primary"
                    :disabled="isChanging"
                    @click="enableNotifications"
                >
                    <b-spinner v-if="isChanging" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-bell mr-1" aria-hidden="true"></i>
                    Включить уведомления
                </b-button>

                <b-button
                    v-if="canDisable"
                    variant="outline-danger"
                    :disabled="isChanging"
                    @click="disableNotifications"
                >
                    <b-spinner v-if="isChanging" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-bell-slash mr-1" aria-hidden="true"></i>
                    Отключить на этом устройстве
                </b-button>

                <b-button
                    v-if="deviceState && deviceState.status === 'denied'"
                    variant="outline-primary"
                    :disabled="isChanging"
                    @click="refreshState(true)"
                >
                    Проверить снова
                </b-button>

                <b-button
                    v-if="showInstallOffer"
                    variant="primary"
                    :disabled="installOpening"
                    @click="openInstallPage"
                >
                    <b-spinner v-if="installOpening" small class="mr-1"></b-spinner>
                    Установить Аргус
                </b-button>
            </div>

            <div
                v-if="deviceState && deviceState.status === 'denied'"
                class="notification-device-settings__hint"
            >
                {{ deniedInstructions }}
            </div>
        </template>
    </section>
</template>

<script>
import InstallAppPreview from '@/components/InstallAppPreview.vue';
import {
    hasTelegramInstallSuccessEligible,
    INSTALLATION_STATE_CHANGED_EVENT,
    isIOSInstallDevice,
    markInstallPromotionSessionDismissed,
    openInstallUrlExternally,
    prepareTelegramInstallHandoff,
    shouldShowInstallPromotion
} from '@/utils/appInstallation';
import {
    clearNotificationOnboardingState,
    disableNotificationOnboarding,
    disableWebPushForCurrentDevice,
    enableWebPushForCurrentDevice,
    isAndroidDevice,
    isIOSDevice,
    prepareWebPushDevice
} from '@/utils/notificationPermission';

export default {
    name: 'NotificationDeviceSettings',
    components: {
        InstallAppPreview
    },
    data() {
        return {
            deviceState: null,
            isLoading: true,
            isChanging: false,
            errorMessage: '',
            installCampaignEligible: false,
            installEligibilityCheckId: 0,
            installOpening: false
        };
    },
    computed: {
        isTelegramWebApp() {
            return this.$store.getters.isTelegramWebApps;
        },
        currentIdentity() {
            const user = this.$store.getters.currentUser;
            return user
                ? `${user.company || ''}:${user.userId || ''}`
                : null;
        },
        onboardingContext() {
            return {
                identity: this.currentIdentity
            };
        },
        usesModernUi() {
            const navigation = this.$store.state.navigationExperience || {};
            return navigation.loaded === true
                && this.$store.getters.navigationReady;
        },
        statusLabel() {
            const labels = {
                unsupported: 'Не поддерживаются',
                'server-unavailable': 'Временно недоступны',
                'subscription-error': 'Нужна синхронизация',
                denied: 'Заблокированы',
                default: 'Не включены',
                'granted-unsubscribed': 'Не подключены',
                subscribed: 'Включены'
            };
            return labels[this.deviceState && this.deviceState.status] || 'Проверяем';
        },
        statusVariant() {
            const status = this.deviceState && this.deviceState.status;
            if (status === 'subscribed') {
                return 'success';
            }
            if (status === 'denied' || status === 'subscription-error') {
                return 'danger';
            }
            if (status === 'default' || status === 'granted-unsubscribed') {
                return 'warning';
            }
            return 'muted';
        },
        statusIcon() {
            const status = this.deviceState && this.deviceState.status;
            if (status === 'subscribed') {
                return 'fas fa-check-circle';
            }
            if (status === 'denied' || status === 'subscription-error') {
                return 'fas fa-exclamation-circle';
            }
            return 'fas fa-bell';
        },
        statusDescription() {
            const status = this.deviceState && this.deviceState.status;
            if (status === 'subscribed') {
                return 'Это устройство готово получать Web Push. Какие именно события отправлять, настроим отдельным этапом.';
            }
            if (status === 'denied') {
                return 'Браузер или операционная система запретили уведомления. Повторно открыть системный запрос из Аргуса нельзя.';
            }
            if (status === 'granted-unsubscribed') {
                return 'Системное разрешение уже выдано, но подписка этого устройства ещё не создана.';
            }
            if (status === 'subscription-error') {
                return 'Разрешение и локальная подписка есть, но Аргус не смог сохранить её на сервере.';
            }
            if (status === 'server-unavailable') {
                if (this.deviceState.config && this.deviceState.config.reason === 'disabled') return 'Push-уведомления пока выключены. Сейчас Аргус уведомляет сообщениями в Telegram.';
                return 'Серверная инфраструктура Web Push пока не настроена или временно недоступна.';
            }
            if (status === 'unsupported') {
                if (this.isTelegramWebApp) {
                    return this.showInstallOffer
                        ? 'Этот клиент Telegram не поддерживает системный Web Push. Для таких уведомлений установите Аргус на экран. Обычные сообщения бота продолжат приходить без дополнительных разрешений.'
                        : 'Этот клиент Telegram не поддерживает системный Web Push. Обычные сообщения бота продолжат приходить без дополнительных разрешений.';
                }
                return this.showInstallOffer
                    ? 'На iPhone и iPad Web Push доступен только после установки Аргуса на экран.'
                    : 'Этот браузер или режим просмотра не поддерживает Web Push.';
            }
            return 'Разрешите уведомления и зарегистрируйте этот браузер одним нажатием.';
        },
        canEnable() {
            return Boolean(
                this.deviceState
                && [
                    'default',
                    'granted-unsubscribed',
                    'subscription-error'
                ].includes(this.deviceState.status)
            );
        },
        canDisable() {
            return Boolean(
                this.deviceState
                && ['subscribed', 'subscription-error'].includes(this.deviceState.status)
                && this.deviceState.subscription
            );
        },
        shouldOfferInstall() {
            return Boolean(
                this.deviceState
                && this.deviceState.status === 'unsupported'
                && !this.deviceState.isInstalled
                && (
                    this.deviceState.isIOS
                    || (this.isTelegramWebApp && isAndroidDevice())
                )
            );
        },
        showInstallOffer() {
            return this.shouldOfferInstall
                && (!this.usesModernUi || this.installCampaignEligible);
        },
        deniedInstructions() {
            if (isIOSDevice()) {
                return 'Откройте «Настройки» → «Уведомления» → «Аргус» и включите допуск уведомлений, затем вернитесь сюда.';
            }
            if (isAndroidDevice()) {
                return 'Зажмите ярлык Аргуса → «О приложении» → «Уведомления» и разрешите их. В браузере это также можно сделать в настройках сайта.';
            }
            return 'Откройте настройки сайта возле адресной строки, разрешите уведомления для Аргуса и обновите страницу.';
        }
    },
    mounted() {
        this.refreshState();
        this.refreshInstallEligibility();
        window.addEventListener('focus', this.onWindowFocus);
        window.addEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.refreshInstallEligibility
        );
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    },
    beforeDestroy() {
        window.removeEventListener('focus', this.onWindowFocus);
        window.removeEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.refreshInstallEligibility
        );
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    },
    methods: {
        async refreshState(force = false) {
            this.isLoading = !this.deviceState;
            this.errorMessage = '';
            try {
                this.deviceState = await prepareWebPushDevice({
                    syncExisting: true,
                    force
                });
            } catch (error) {
                this.deviceState = {
                    status: 'server-unavailable',
                    subscription: null,
                    error
                };
                this.errorMessage = 'Не удалось проверить состояние уведомлений.';
            } finally {
                this.isLoading = false;
            }
        },
        async refreshInstallEligibility() {
            if (!this.usesModernUi) {
                this.installCampaignEligible = false;
                return;
            }
            if (
                this.isTelegramWebApp
                && isIOSInstallDevice()
                && !hasTelegramInstallSuccessEligible()
            ) {
                this.installCampaignEligible = false;
                return;
            }
            const checkId = ++this.installEligibilityCheckId;
            const eligible = await shouldShowInstallPromotion();
            const handoff = eligible && this.isTelegramWebApp
                ? await prepareTelegramInstallHandoff()
                : null;
            if (checkId !== this.installEligibilityCheckId) return;
            this.installCampaignEligible = Boolean(
                eligible
                && (!this.isTelegramWebApp || handoff)
            );
        },
        async openInstallPage() {
            if (this.isTelegramWebApp && this.usesModernUi) {
                if (this.installOpening) return;
                this.installOpening = true;
                this.errorMessage = '';
                try {
                    const opened = await openInstallUrlExternally();
                    if (!opened) {
                        this.errorMessage = 'Не удалось подготовить безопасный вход. Проверьте интернет и повторите.';
                    }
                } finally {
                    this.installOpening = false;
                }
                return;
            }
            if (this.usesModernUi) {
                markInstallPromotionSessionDismissed();
            }
            this.$router.push('/install');
        },
        async enableNotifications() {
            if (this.isChanging) {
                return;
            }
            this.isChanging = true;
            this.errorMessage = '';
            let operationError = '';
            try {
                await enableWebPushForCurrentDevice();
                clearNotificationOnboardingState(
                    this.onboardingContext
                );
            } catch (error) {
                operationError =
                    'Не удалось подключить уведомления. Проверьте разрешения и интернет.';
            } finally {
                await this.refreshState(true);
                if (operationError && this.deviceState.status !== 'denied') {
                    this.errorMessage = operationError;
                }
                this.isChanging = false;
            }
        },
        async disableNotifications() {
            if (this.isChanging) {
                return;
            }
            this.isChanging = true;
            this.errorMessage = '';
            let operationError = '';
            try {
                await disableWebPushForCurrentDevice();
            } catch (error) {
                operationError =
                    'Локальная подписка отключена, но сервер не подтвердил удаление. Аргус повторит синхронизацию позже.';
            } finally {
                disableNotificationOnboarding(
                    this.onboardingContext
                );
                await this.refreshState(true);
                if (operationError) {
                    this.errorMessage = operationError;
                }
                this.isChanging = false;
            }
        },
        onWindowFocus() {
            this.refreshState(true);
        },
        onVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.refreshState(true);
            }
        }
    }
};
</script>

<style scoped>
.notification-device-settings {
    border-color: #cfe0f8;
    background: linear-gradient(145deg, #f8fbff, #fff);
}

.notification-device-settings__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}

.notification-device-settings__header h6 {
    margin: 0;
}

.notification-device-settings__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 700;
    border-radius: 999px;
}

.notification-device-settings__status--success {
    color: #176b3a;
    background: #dff5e8;
}

.notification-device-settings__status--warning {
    color: #8a5a00;
    background: #fff0c7;
}

.notification-device-settings__status--danger {
    color: #9b2c2c;
    background: #ffe4e4;
}

.notification-device-settings__status--muted {
    color: #5e6b79;
    background: #edf1f5;
}

.notification-device-settings__loading,
.notification-device-settings__description {
    color: #5f6f82;
}

.notification-device-settings__description {
    margin: 0;
    line-height: 1.5;
}

.notification-device-settings__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 16px;
}

.notification-device-settings__install-preview {
    max-width: 380px;
    margin-top: 16px;
}

.notification-device-settings__hint {
    margin-top: 14px;
    padding: 12px 14px;
    color: #5f4a19;
    font-size: 14px;
    line-height: 1.45;
    background: #fff8df;
    border-radius: 12px;
}

@media (max-width: 575px) {
    .notification-device-settings__header {
        flex-direction: column;
    }

    .notification-device-settings__actions,
    .notification-device-settings__actions .btn {
        width: 100%;
    }
}
</style>
